/**
 * useSessionTimeout Hook
 * Manages session timeout with inactivity detection and warning dialogs.
 *
 * Uses refs for callback stability to prevent the dependency chain from
 * causing timers to restart on every render.
 *
 * Timeline (with default 5min inactivity + 5min warning):
 *   0:00 ── user activity resets ──> 5:00 warning shown ──> 10:00 auto-logout
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getSessionTimeouts, ACTIVITY_EVENTS } from '../constants/session';

export interface UseSessionTimeoutOptions {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Callback when session expires (user should be logged out) */
  onTimeout: () => void;
  /** Time of inactivity (ms) before showing the warning dialog */
  inactivityTimeout?: number;
  /** Duration (ms) the warning dialog is shown before auto-logout */
  warningDuration?: number;
  /** Whether timeout is enabled */
  enabled?: boolean;
}

export interface UseSessionTimeoutResult {
  /** Whether the timeout warning dialog should be shown */
  showWarning: boolean;
  /** Extend the session (dismiss warning and reset timers) */
  extendSession: () => void;
  /** Manually trigger logout */
  logout: () => void;
  /** Time remaining until auto-logout (in seconds) */
  timeRemaining: number;
  /** Reset all timers (e.g., after user activity) */
  resetTimers: () => void;
}

export function useSessionTimeout(options: UseSessionTimeoutOptions): UseSessionTimeoutResult {
  const {
    isAuthenticated,
    onTimeout,
    inactivityTimeout: customInactivityTimeout,
    warningDuration: customWarningDuration,
    enabled = true,
  } = options;

  // Get timeout values (custom or from constants)
  const defaults = getSessionTimeouts();
  const inactivityTimeout = customInactivityTimeout ?? defaults.inactivityTimeout;
  const warningDuration = customWarningDuration ?? defaults.warningDuration;

  // State
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  // Refs for timers
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoLogoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastResetRef = useRef<number>(0);

  // Refs for stable callbacks — breaks the dependency chain so the useEffect
  // doesn't re-run when onTimeout or showWarning changes
  const onTimeoutRef = useRef(onTimeout);
  const showWarningRef = useRef(showWarning);

  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);
  useEffect(() => { showWarningRef.current = showWarning; }, [showWarning]);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
    if (autoLogoutTimeoutRef.current) {
      clearTimeout(autoLogoutTimeoutRef.current);
      autoLogoutTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Handle session expiry — uses ref so this callback is stable
  const handleTimeout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    showWarningRef.current = false;
    setTimeRemaining(0);
    onTimeoutRef.current();
  }, [clearAllTimers]);

  // Start the inactivity timer
  const startInactivityTimer = useCallback(() => {
    // Clear any existing inactivity timer
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    // After `inactivityTimeout` ms of no activity, show the warning
    inactivityTimeoutRef.current = setTimeout(() => {
      // Show warning dialog
      setShowWarning(true);
      showWarningRef.current = true;
      setTimeRemaining(Math.ceil(warningDuration / 1000));

      // Start countdown (updates every second)
      countdownIntervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-logout after the warning duration elapses
      autoLogoutTimeoutRef.current = setTimeout(() => {
        handleTimeout();
      }, warningDuration);
    }, inactivityTimeout);
  }, [inactivityTimeout, warningDuration, handleTimeout]);

  // Extend session (user clicked "Stay Logged In")
  const extendSession = useCallback(() => {
    setShowWarning(false);
    showWarningRef.current = false;
    setTimeRemaining(0);

    // Clear only the warning-related timers
    if (autoLogoutTimeoutRef.current) {
      clearTimeout(autoLogoutTimeoutRef.current);
      autoLogoutTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    // Restart the inactivity timer
    startInactivityTimer();
  }, [startInactivityTimer]);

  // Manual logout
  const logout = useCallback(() => {
    handleTimeout();
  }, [handleTimeout]);

  // Reset inactivity timer on user activity — stable because it uses refs
  const resetTimers = useCallback(() => {
    // Don't reset if warning is showing (user must explicitly extend or be logged out)
    if (showWarningRef.current) return;

    // Throttle resets to at most once per second to avoid excessive timer churn
    const now = Date.now();
    if (now - lastResetRef.current < 1000) return;
    lastResetRef.current = now;

    startInactivityTimer();
  }, [startInactivityTimer]);

  // Set up activity listeners and initial timer
  useEffect(() => {
    if (!isAuthenticated || !enabled) {
      clearAllTimers();
      setShowWarning(false);
      showWarningRef.current = false;
      setTimeRemaining(0);
      return;
    }

    // Start inactivity timer on mount / auth change
    startInactivityTimer();

    // Activity event handler
    const handleActivity = () => {
      if (!showWarningRef.current) {
        resetTimers();
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, enabled, clearAllTimers, startInactivityTimer, resetTimers]);

  // Clean up when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      showWarningRef.current = false;
      setTimeRemaining(0);
    }
  }, [isAuthenticated, clearAllTimers]);

  return {
    showWarning,
    extendSession,
    logout,
    timeRemaining,
    resetTimers,
  };
}

export default useSessionTimeout;
